import { OnDestroy, OnInit, Directive } from '@angular/core';
import { DataQuery } from '../../classroom360/data-query/models/data-query.model';
import { DataQueryInformation } from '../../classroom360/data-query/models/data-query-information';
import { DataQueryPreferences } from '../user-preferences/data-query-preferences.model';
import { DataQueryField } from '../../classroom360/data-query/models/data-query-field.model';
import { DataQueryService } from '../../classroom360/data-query/data-query.service';
import { Observable, Subscription } from 'rxjs';
import { GeneralPreferences } from '../user-preferences/general-preferences.model';
import { UserPreferencesService } from '../user-preferences/user-preferences.service';
import { DataQueryFieldFilter } from '../../classroom360/data-query/models/data-query-field-filter.model';
import { LoginService } from '../../shared-modules/login/login.service';
import { DataQueryResult } from '../../classroom360/data-query/models/data-query-result.model';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { LookupService } from '../lookup/lookup.service';
import { YearService } from '../year/year.service';
import { InsertFieldDialogComponent } from '../../classroom360/data-query/data-query-settings-for-export/insert-field-dialog/insert-field-dialog.component';
import * as _ from 'lodash';
import * as moment from 'moment';
import { ErrorList } from '../controls/error-prompt/error-list.model';

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export class AdvancedFilterComponent implements OnInit, OnDestroy {

    // Data Query Properties
    private _currentQuery = new DataQuery();
    private _information = new DataQueryInformation();
    private _preferences = new DataQueryPreferences();

    // Initialization Properties
    title: string;
    recentFilters: DataQuery[] = [];
    fieldsWithoutFilters: string[] = [];

    // Other Properties
    subscriptions: Subscription[] = [];
    generalPreferences: GeneralPreferences;
    allFilters: DataQueryFieldFilter[] = [];
    originalQuery = new DataQuery();

    advancedStudentSearchErrors: ErrorList[] = [];

    // Class Functions
    constructor(
        public dataQueryService: DataQueryService,
        public userPreferencesService: UserPreferencesService,
        public loginService: LoginService,
        public dialogBox: MatDialogRef<AdvancedFilterComponent>,
        public lookupService: LookupService,
        public yearService: YearService,
        public dialog: MatDialog
    ) { }

    get currentQuery() {
        return this._currentQuery;
    }

    set currentQuery(value: DataQuery) {
        this.dataQueryService.checkDeactivationAndChange(value);
    }

    get information() {
        return this._information;
    }

    get preferences() {
        return this._preferences;
    }

    get filterFields(): DataQueryField[] {
        return this.currentQuery.filterFields;
    }

    set filterFields(value: DataQueryField[]) {
        this.currentQuery.filterFields.splice(0, this.currentQuery.filterFields.length);
        Object.assign(this.currentQuery.filterFields, value);
    }

    get filterTranslation() {
        return this.currentQuery ? this.currentQuery.filterTranslation : '';
    }

    // Event Functions
    ngOnInit() {
        this.generalPreferences = this.userPreferencesService.generalPreferences;
        this.initialize();

        // Utility function to load query
        const loadQuery = () => {

            // Only perform opreation if there is the proper data
            if (this.preferences && this.information) {

                // If our query is not set...
                if (this.currentQuery.filterFields.length == 0) {

                    // Load default query
                    if (this.dataQueryService.courseSearchQuery) {
                        this.dataQueryService.loadQuery(this.dataQueryService.courseSearchQuery);
                    } else {

                        // Adding default fields
                        this.dataQueryService.checkDefaultFilterFields(
                            null,
                            (field: DataQueryField) => {
                                const addField = field.duplicate();
                                this.addField(addField, !this.fieldsWithoutFilters.includes(addField.FieldName));
                                this.setFilterValue(addField, true);
                            }
                        );
                    }
                }

                // If our query is set...
                else {

                    // Check default fields
                    this.dataQueryService.checkDefaultFilterFields(
                        this.currentQuery.filterFields,
                        (field: DataQueryField) => {
                            this.setFilterValue(field, false);
                        }
                    );
                }

                // Setting the filters for the table
                this.setAllFilters();

                // Updating the SQL translation
                this.dataQueryService.updateFilter();
                this.dataQueryService.pageSize = this.dataQueryService.initialPageSize;
            }
        };

        // Performing subscriptions
        this.subscriptions.push(

            // Syncing properties
            this.dataQueryService.observableCurrentQueryStream$.subscribe(query => {
                this._currentQuery = query;
                this.originalQuery = _.cloneDeep(query);
                loadQuery();
                this.setAllFilters();
                this._currentQuery.CourseSearch = this.dataQueryService.isCourseSearch;
                this._currentQuery.StudentSearch = this.dataQueryService.isStudentSearch;
                this._currentQuery.StaffSearch = this.dataQueryService.isStaffSearch;
            }),
            this.dataQueryService.observableInformationStream$.subscribe(information => {
                this._information = information;
                loadQuery();
            }),
            this.dataQueryService.observablePreferencesStream$.subscribe(preferences => {
                this._preferences = preferences;
                loadQuery();
            }),
            this.userPreferencesService.observablegeneralPreferencesStream$.subscribe(generalPreferences => {
                this.generalPreferences = generalPreferences;
                loadQuery();
            }),

            // Obtaining data
            this.dataQueryService.getInformation().subscribe(),
            this.dataQueryService.getPreferences().subscribe()
        );
    }

    ngOnDestroy() {
        // Unsubscribing to all subscriptions
        this.subscriptions.forEach(subscription => {
            subscription.unsubscribe();
        });
        this.takeDown();
    }

    addFilter() {
        // PRE: the add filter button is clicked
        // POST: opens a dialog to select a field. Adds the selected field as a filter

        // We need to ensure we have all our fields properly set as disabled or enabled
        this.dataQueryService.disabledFields = {};
        this.information.fields.forEach(field => {
            this.dataQueryService.checkDisabledField(field);
        });

        // Opening the dialog with those disabled fields
        const insertFieldDialog = this.dialog.open(InsertFieldDialogComponent, {
            disableClose: true,
            data: {
                information: this.information,
                isCourseSearch: this.dataQueryService.isCourseSearch,
                isStudentSearch: this.dataQueryService.isStudentSearch,
                isStaffSearch: this.dataQueryService.isStaffSearch,
                disabledFields: this.dataQueryService.disabledFields
            }
        });
        this.subscriptions.push(
            insertFieldDialog.afterClosed().subscribe((field: DataQueryField) => {
                if (field) {
                    field = field.duplicate();
                    this.currentQuery.queryChanged = true;
                    this.filterFields.push(field);
                    this.obtainFilterOptions(field, filterOptions => {
                        this.setFilterField(filterOptions, field);
                        this.setAllFilters();
                    });
                }
            })
        );
    }

    apply() {
        this.advancedStudentSearchErrors = [];
        // Code for invalid Enrollment Date
        for (let i = 0; i < this.currentQuery.filterFields.length; i++) {
            if (this.currentQuery.filterFields[i].Alias == 'Enrollment Date') {
                for (let j = 0; j < this.currentQuery.filterFields[i].Filters.length; j++) {
                    if (this.currentQuery.filterFields[i].Filters[j].Value && this.currentQuery.filterFields[i].Filters[j].Value != null) {
                        // alert(this.currentQuery.filterFields[i].Filters[j].Value);
                        const dateValue = this.currentQuery.filterFields[i].Filters[j].Value.toString();
                        if (!moment(dateValue, ['MM/DD/YYYY']).isValid()
                            || moment(dateValue, ['MM/DD/YYYY']).isBefore(moment('01/01/1900', ['MM/DD/YYYY']))
                            || (moment('01/01/2070', ['MM/DD/YYYY'])).isBefore(moment(dateValue, ['MM/DD/YYYY']))) {
                            this.advancedStudentSearchErrors.push({
                                fieldName: 'Enrollment Date',
                                fieldRef: null,
                                errorMsg: 'Invalid'
                            });
                            return 0;
                        }
                    }

                    if (this.currentQuery.filterFields[i].Filters[j].Value2 && this.currentQuery.filterFields[i].Filters[j].Value2 != null) {
                        // alert(this.currentQuery.filterFields[i].Filters[j].Value);
                        const dateValue = this.currentQuery.filterFields[i].Filters[j].Value2.toString();
                        if (!moment(dateValue, ['MM/DD/YYYY']).isValid()
                            || moment(dateValue, ['MM/DD/YYYY']).isBefore(moment('01/01/1900', ['MM/DD/YYYY']))
                            || (moment('01/01/2070', ['MM/DD/YYYY'])).isBefore(moment(dateValue, ['MM/DD/YYYY']))) {
                            this.advancedStudentSearchErrors.push({
                                fieldName: 'Enrollment Date',
                                fieldRef: null,
                                errorMsg: 'Invalid'
                            });
                            return 0;
                        }
                    }
                }
            }
        }

        // Code for invalid Birth Date
        for (let i = 0; i < this.currentQuery.filterFields.length; i++) {
            if (this.currentQuery.filterFields[i].Alias == 'Birth Date') {
                for (let j = 0; j < this.currentQuery.filterFields[i].Filters.length; j++) {
                    if (this.currentQuery.filterFields[i].Filters[j].Value && this.currentQuery.filterFields[i].Filters[j].Value != null) {
                        // alert(this.currentQuery.filterFields[i].Filters[j].Value);
                        const dateValue = this.currentQuery.filterFields[i].Filters[j].Value.toString();
                        if (!moment(dateValue, ['MM/DD/YYYY']).isValid()
                            || moment(dateValue, ['MM/DD/YYYY']).isBefore(moment('01/01/1900', ['MM/DD/YYYY']))
                            || (moment('01/01/2070', ['MM/DD/YYYY'])).isBefore(moment(dateValue, ['MM/DD/YYYY']))) {
                            this.advancedStudentSearchErrors.push({
                                fieldName: 'Birth Date',
                                fieldRef: null,
                                errorMsg: 'Invalid'
                            });
                            return 0;
                        }
                    }

                    if (this.currentQuery.filterFields[i].Filters[j].Value2 && this.currentQuery.filterFields[i].Filters[j].Value2 != null) {
                        // alert(this.currentQuery.filterFields[i].Filters[j].Value);
                        const dateValue = this.currentQuery.filterFields[i].Filters[j].Value2.toString();
                        if (!moment(dateValue, ['MM/DD/YYYY']).isValid()
                            || moment(dateValue, ['MM/DD/YYYY']).isBefore(moment('01/01/1900', ['MM/DD/YYYY']))
                            || (moment('01/01/2070', ['MM/DD/YYYY'])).isBefore(moment(dateValue, ['MM/DD/YYYY']))) {
                            this.advancedStudentSearchErrors.push({
                                fieldName: 'Birth Date',
                                fieldRef: null,
                                errorMsg: 'Invalid'
                            });
                            return 0;
                        }
                    }
                }
            }
        }

        // PRE: The apply button is clicked
        // POST: Shows the warning dialog for saving and/or continuing if the query is changed.
        //       After, gets the results and closes with them
        let schoolIds = '';
        this.yearService.schools.forEach(school => {
            if (school.SchoolName == this.dataQueryService.studentSearchFilters.ddlSchool || this.dataQueryService.studentSearchFilters.ddlSchool == '') {
                schoolIds = schoolIds + (schoolIds == '' ? school.Id.toString() : ',' + school.Id.toString());
            }
        });
        let hasErrors = false;
        for (let i = 0; i < this.currentQuery.filterFields.length; i++) {
            for (let j = 0; j < this.currentQuery.filterFields[i].Filters.length; j++) {
                if (this.currentQuery.filterFields[i].Filters[j].showFilterError1 || this.currentQuery.filterFields[i].Filters[j].showFilterError2) {
                    hasErrors = true;
                    break;
                }
            }
            if (hasErrors) {
                break;
            }
        }
        if (!hasErrors) {
            this.checkCurrentQueryWarning(() => {
                this.dataQueryService.getResults(
                    false,
                    (results: DataQueryResult) => {
                        console.log(results);
                        this.dialogBox.close(results);
                    },
                    (dataQueryFormError: any) => {
                        if (dataQueryFormError.blankAlias) {
                            // TODO: Error circle?
                        }
                        if (dataQueryFormError.duplicateAlias) {
                            // TODO: Error circle?
                        }
                    },
                    true,
                    true,
                    schoolIds
                );
            });
        }
    }

    cancel() {
        // PRE: the cancel button is clicked
        // POST: checks if we need to save and closes the dialog
        this.checkCurrentQueryWarning(() => {
            Object.assign(this.currentQuery, this.originalQuery);
            this.dialogBox.close();
        });
    }

    // Methods
    addField(field: DataQueryField, withFilter = true) {
        // PRE: a field to add to the query
        // POST: adds the field to the query appropriately
        field.IsDefaultFilterField = true;
        const addedField = field.duplicate();
        addedField.SortDirection = 'None';
        if (!this.currentQuery.reportFields.find(reportField => reportField.ID == addedField.ID)) {
            this.currentQuery.reportFields.push(addedField);
        }
        if (!this.currentQuery.sortFields.find(sortField => sortField.ID == addedField.ID)) {
            this.currentQuery.sortFields.push(addedField);
        }
        addedField.CategoryName = addedField.category.CategoryName;
        addedField.ObjectName = addedField.object.ObjectName;
        if (withFilter) {
            this.addDefaultFilterField(field);
        }
    }

    addDefaultFilterField(field: DataQueryField) {
        // POST: adds the default field filter for the field, including its enumeration values
        this.filterFields.push(field);
        this.obtainFilterOptions(field, filterOptions => {
            this.setFilterField(filterOptions, field.ID);
            this.setFilterValue(field, true);
            this.setAllFilters();
        });
    }

    checkCurrentQueryWarning(callback: Function) {
        // PRE: a callback function we're checking for
        // POST: shows the warning if needed and calls the callback if the user continues
        if (this.currentQuery.queryChanged) {
            const warning = this.dataQueryService.showWarning();
            if (warning instanceof Observable) {
                warning.subscribe(result => {
                    if (result) {
                        callback();
                    }
                });
            } else {
                if (warning) {
                    callback();
                }
            }
        } else {
            callback();
        }
    }

    obtainFilterOptions(field: DataQueryField, _callback: Function) {
        let filterOptions = [];
        if (field.FilterType == 3) {
            if (this.loginService.LoginSettings.V10 || field.FieldName != 'Pri Enrl Site Name') {
                this.subscriptions.push(
                    this.lookupService.getDataQueryFieldLookupReadByFieldId(field.ID, field.CategoryID).subscribe(
                        results => {
                            filterOptions = results;
                        },
                        () => { },
                        () => {
                            _callback(filterOptions);
                        }
                    )
                );
            } else {
                this.subscriptions.push(
                    this.yearService.getStudentSearchSchoolByYearId(this.yearService.defaultYear.Id).subscribe(
                        results => {
                            for (let i = 0; i < results.length; i++) {
                                filterOptions.push({
                                    Id: results[i].SchoolId, Code: '',
                                    CodeDescription: results[i].SchoolName,
                                    Sequence: i + 2
                                });
                            }
                            const allSchools = {
                                Id: 0,
                                Code: '',
                                CodeDescription: 'All',
                                Sequence: 1
                            };
                            filterOptions.unshift(allSchools);	// Does this work???
                        },
                        () => { },
                        () => {
                            _callback(filterOptions);
                        }
                    )
                );
            }
        } else {
            _callback(filterOptions);
        }
    }

    setFilterField(filterOptions, fieldId) {
        // PRE: the options for the filter and the filter's field ID
        // POST: sets the filter for the field
        let filter = new DataQueryFieldFilter();
        let field: DataQueryField;
        if (typeof fieldId === 'number') {
            field = this.filterFields.find(f => f.ID == fieldId);
        } else if (fieldId.ID) {
            field = this.filterFields.find(f => f.ID == fieldId.ID);
        }

        // Creating new filter if one doesn't exist
        if (field.Filters.length == 0) {

            // Setting intermediary values
            filter.FilterID = DataQueryService.filterFieldId;
            filter.FilterOptions = filterOptions;
            this.setFilterPropertiesSwitch(field, filter);

            // Setting the filter fields
            this.dataQueryService.setShowOnReportParameters();
            filter.field = field;
            DataQueryService.filterFieldId = DataQueryService.filterFieldId + 1;
        }

        // Adding options if a filter does exist
        else if (filterOptions.length > 0) {
            filter = field.Filters[0];
            filter.FilterOptions = filterOptions;
        }

        // Added the filter if it isn't there
        if (!this.allFilters.includes(filter)) {
            this.allFilters.push(filter);
        }
        this.dataQueryService.setShowOnReportParameters();
    }

    setAllFilters() {
        // POST: sets the "All Filters" array, which controls the table data
        const allFilters = [];
        this.filterFields.forEach(field => {
            field.Filters.forEach(filter => {
                allFilters.push(filter);

                // Ensuring the filter is associated
                filter.field = field;

                // Setting the operator to the first viable one if one doesn't exist
                if (!filter.Operator) {
                    const operators = filter.getFilterOperators();
                    filter.Operator = operators[0];
                }
            });
        });
        this.allFilters = allFilters;

        // Updating the SQL translation
        this.dataQueryService.updateFilter();
        this.dataQueryService.pageSize = this.dataQueryService.initialPageSize;
    }

    setFilterValue(field: DataQueryField, updateUsing: boolean) {
        // POST: sets the value of each filter
        if (field.Filters.length > 0) {
            const filter = field.Filters[0];
            if (field.IsDefaultFilterField) {
                if (this.loginService.LoginSettings.V10 == false) {
                    switch (field.DisplayName) {
                        case 'Student ID':
                            if (filter.UseFilterForStudentSearch) {
                                this.dataQueryService.studentSearchFilters.StudentNumber = filter.Value;
                            }
                            break;
                        case 'Name - First':
                            if (filter.UseFilterForStudentSearch) {
                                this.dataQueryService.studentSearchFilters.FirstName = filter.Value;
                            }
                            break;
                        case 'Name - Last':
                            if (filter.UseFilterForStudentSearch) {
                                this.dataQueryService.studentSearchFilters.LastName = filter.Value;
                            }
                            break;
                        case 'Grade':
                            if (filter.UseFilterForStudentSearch) {
                                this.dataQueryService.studentSearchFilters.Grade = filter.Value;
                            }
                            break;
                        case 'Enrollment Date':
                            if (filter.UseFilterForStudentSearch) {
                                this.dataQueryService.studentSearchFilters.EnrollmentDate = filter.Value;
                            }
                            break;
                        case 'Birth Date':
                            if (filter.UseFilterForStudentSearch) {
                                this.dataQueryService.studentSearchFilters.DOB = filter.Value;
                            }
                            break;
                    }
                }
                this.setValueSwitch(field, filter, updateUsing);
            }
        }
    }

    // Override Functions
    initialize() {
        // NOTE: THIS SHOULD BE OVERRIDDEN
    }

    takeDown() {
        // NOTE: THIS SHOULD BE OVERRIDEN
    }

    setValueSwitch(field: DataQueryField, filter: DataQueryFieldFilter, updateUsing: boolean) {
        // NOTE: THIS SHOULD BE OVERRIDDEN
    }

    setFilterPropertiesSwitch(field: DataQueryField, filter: DataQueryFieldFilter) {
        // NOTE: THIS SHOULD BE OVERRIDDEN
    }
}
